import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { useAnchor } from "hooks";
import { getImageFormat } from "lib/utils";
import { Features1Content } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Features1Content;
}

const Features1: VFC<Props> = withErrorBoundary(({ content: { features1Feature, features1Link } }) => {
  useAnchor("features1");

  return (
    <StyledFeatures className="features1 Content">
      <ul className="Features1__list">
        {features1Feature.map(
          ({
            id,
            title,
            description,
            icon: {
              data: {
                icon: { ext, ...icon },
              },
            },
          }) => (
            <li key={id} className="Features1__listItem">
              <LegacyImage
                {...icon}
                layout="fill"
                transform={{ format: getImageFormat(ext) }}
                className="Features1__iconContainer"
              />
              <h3 className="Features1__listItemTitle Font_24_32_600">{title}</h3>
              <Markdown className="Features1__listItemDescription Color_secondary Font_18_26_450">
                {description}
              </Markdown>
            </li>
          )
        )}
      </ul>
      {features1Link && <Markdown className="Features1__link Font_12_16_450">{features1Link}</Markdown>}
    </StyledFeatures>
  );
}, {});

const StyledFeatures = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Features1__list {
    display: flex;
    justify-content: space-between;
  }

  .Features1__listItem {
    flex: 1;
  }

  .Features1__listItem:not(:first-child) {
    margin-left: 32px;
  }

  .Features1__iconContainer {
    width: 64px;
    height: 64px;
    position: relative;
  }

  .Features1__listItemTitle {
    margin-top: 32px;
  }

  .Features1__listItemDescription {
    margin-top: 16px;
    max-width: 360px;

    a {
      text-decoration: underline;

      :hover {
        text-decoration: none;
      }

      :active {
        opacity: 0.7;
      }
    }
  }

  .Features1__link {
    margin-top: 80px;
    color: ${({ theme }) => theme.colors.fog["50"]};
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Features1__list {
      flex-direction: column;
    }

    .Features1__listItem:not(:first-child) {
      margin-top: 40px;
      margin-left: unset;
    }

    .Features1__iconContainer {
      width: 60px;
      height: 60px;
    }

    .Features1__listItemTitle {
      margin-top: 16px;
    }

    .Features1__listItemDescription {
      margin-top: 8px;
      max-width: unset;
    }

    .Features1__link {
      margin-top: 64px;
    }
  }
`;

gql`
  fragment Features1Content on ComponentFeaturesFeatures1 {
    features1Link
    features1Feature {
      id
      title
      description
      icon {
        data {
          icon: attributes {
            src: hash
            alt: alternativeText
            ext
          }
        }
      }
    }
  }
`;

export { Features1 };
export type { Props as Features1Props };
